/**
 * The dock header: ONE 40 px row that never wraps (spec §B1).
 *
 *   [eyebrow · title · scope · range …truncate…] [⚠ chips] [verdict][KPI][KPI]…  [ⓘ][⤢][✕]
 *
 * The old headers wrapped (`flex-wrap` on three nested rows), so at 1000 px
 * they ate 180–210 px of a 280 px dock and left the chart a 0 px plot (audit
 * A1). This one is `@container`-queried instead: what does not fit is HIDDEN,
 * lowest priority first, and moves into the ⓘ Details popover — it never
 * pushes a second row.
 *
 *  - `DockIdentity` truncates. The title keeps a `12ch` floor (the fix for
 *    "Clea…" is a minimum width, not wrapping); the scope and the range
 *    drop below a 1100 px dock. Both stay in Details.
 *  - `DockKpiStrip` hides KPIs by `priority` with container-query classes, and
 *    publishes the same thresholds to JS (`isKpiHidden`) so
 *    `DockDetailsPopover` can list exactly the KPIs that are off screen.
 *  - `DockPanelButtons` is ⓘ ⤢ ✕ in the row's normal flow. The absolute
 *    `top-4 right-8` corner buttons and the `pr-16` the header needed to stay
 *    clear of them are gone.
 */
import {
  Children,
  createContext,
  isValidElement,
  use,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { AlertTriangle, Maximize2, Minimize2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

import { DOCK_CONTROL, DOCK_TEXT } from './dockStyles';

// ------------------------------------------------------------ width context

/**
 * The header's measured width, px — `null` until a ResizeObserver has
 * reported (jsdom, the first paint). CSS hides the KPIs; this tells JS which
 * ones CSS hid, so Details can show them. `null` means "assume everything
 * fits", which is what the CSS does before layout too.
 */
const DockHeaderWidthContext = createContext<number | null>(null);

/** The enclosing `DockHeader`'s width, or `null` outside one / before layout. */
export const useDockHeaderWidth = (): number | null => use(DockHeaderWidthContext);

// ------------------------------------------------------------- KPI priority

/**
 * Which KPIs give way first. `0` never hides; `1` hides first, then `2`, then
 * `3`. The spec's drop order maps onto it directly — clearance: Affected
 * intervals `1`, At station `2`; station volumes: Coverage `1`, Fill `2`.
 */
export type DockKpiPriority = 0 | 1 | 2 | 3;

/**
 * Below what HEADER width (px, the `@container`) each priority hides. Chosen
 * against the real strip at its widest — verdict chip + four KPIs + ⓘ ⤢ ✕ is
 * ≈ 620 px, leaving the identity its `12ch` floor plus the eyebrow from about
 * 1000 px down. Keep in step with `KPI_HIDE_CLASS`: Tailwind needs the class
 * names spelled out in full to generate them, so they cannot be built from
 * these numbers.
 */
export const KPI_HIDE_BELOW_PX: Record<Exclude<DockKpiPriority, 0>, number> = { 1: 1000, 2: 860, 3: 720 };

/**
 * Container-query hide classes for anything ELSE in a dock's chrome that must
 * give way on a narrow dock — a contextual tab-bar tool, a segmented label.
 * They query the nearest `@container`: the header inside `DockHeader`, the
 * tab bar inside `DockTabBar`. Spelled out for Tailwind's scanner; pick the
 * rung, do not build a class string.
 */
export const DOCK_CQ_HIDE = {
  below1200: '@max-[1200px]:hidden',
  below1100: '@max-[1100px]:hidden',
  below1000: '@max-[1000px]:hidden',
  below900: '@max-[900px]:hidden',
  below800: '@max-[800px]:hidden',
  below700: '@max-[700px]:hidden',
} as const;

const KPI_HIDE_CLASS: Record<DockKpiPriority, string> = {
  0: '',
  1: '@max-[1000px]:hidden',
  2: '@max-[860px]:hidden',
  3: '@max-[720px]:hidden',
};

/** Whether a KPI of this priority is hidden at this header width. */
export const isKpiHidden = (priority: DockKpiPriority, width: number | null): boolean =>
  priority !== 0 && width !== null && width < KPI_HIDE_BELOW_PX[priority];

export interface DockKpiItem {
  /** React key, and how Details tells two hidden KPIs apart. */
  key: string;
  priority: DockKpiPriority;
  /** Usually a `<DockKpi>`; the verdict tile is a `<VerdictChip>`. */
  node: ReactNode;
}

// ------------------------------------------------------------------ header

export interface DockHeaderProps {
  /** `<DockIdentity>`. Takes the row's slack and truncates. */
  identity: ReactNode;
  /**
   * Warnings — a stale alignment, low coverage — as compact
   * `<DockChip tone='warn'>`s with the long text in their `title`, most
   * serious first (a stale alignment leads). Placed after the identity in
   * their own group and NEVER hidden: a reason to distrust every figure beside
   * it is not a parameter (the docks' standing rule). When the header cannot
   * fit them all beside the identity's floor, they collapse into ONE `⚠ N`
   * chip whose popover lists every warning with its full text — still on
   * screen, still one click from each action a chip carries.
   */
  warnings?: ReactNode;
  /** The collapsed `⚠ N` chip's test id. Defaults to `testId` with `-header`
   *  swapped for `-warnings` (`sv-warnings`, `clearance-warnings`). */
  warningsTestId?: string;
  /** `<DockKpiStrip>`. */
  kpis?: ReactNode;
  /** `<DockPanelButtons>`. */
  buttons?: ReactNode;
  testId?: string;
  className?: string;
}

/**
 * The least the identity keeps beside the warnings before they collapse, px:
 * the title's `12ch` floor at 13 px semibold (~96 px) plus the row gap. The
 * eyebrow, scope and range have their own container-query rungs.
 */
const IDENTITY_FLOOR_PX = 108;
/** `px-3` either side, and the three `gap-3`s between the row's four groups. */
const ROW_CHROME_PX = 24 + 3 * 12;

export const DockHeader = ({
  identity,
  warnings,
  warningsTestId,
  kpis,
  buttons,
  testId,
  className,
}: DockHeaderProps) => {
  const { t } = useTranslation('project');
  const [width, setWidth] = useState<number | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const warnRef = useRef<HTMLDivElement | null>(null);
  const kpisRef = useRef<HTMLDivElement | null>(null);
  const buttonsRef = useRef<HTMLDivElement | null>(null);
  const warningItems = Children.toArray(warnings);
  /** Which warnings are showing — a change re-measures them laid out flat. */
  const signature = warningItems.map((w) => (isValidElement(w) ? String(w.key) : '·')).join('|');
  /**
   * Collapse is decided AFTER layout, from the flat group's natural width
   * against what the row can spare once the KPIs, the panel buttons and the
   * identity's floor have theirs. The available width does not depend on the
   * decision (the identity takes the slack either way), so it cannot
   * oscillate. A new set of warnings renders flat once (layout effect, before
   * paint) to be measured; the measurement is kept for that set.
   */
  const measured = useRef<{ signature: string; width: number } | null>(null);
  const [mode, setMode] = useState<{ signature: string; collapsed: boolean }>({ signature: '', collapsed: false });
  const collapsed = warningItems.length > 0 && mode.signature === signature && mode.collapsed;
  useLayoutEffect(() => {
    if (warningItems.length === 0 || width === null) return;
    if (warnRef.current) measured.current = { signature, width: warnRef.current.scrollWidth };
    const natural = measured.current?.signature === signature ? measured.current.width : null;
    if (natural === null) return;
    // What the identity cannot give up: the title's floor plus whichever of
    // the eyebrow and the range its own container-query rungs still show
    // (`offsetWidth` is 0 for a `display: none` part). Both are whole-or-
    // nothing — a range cut to "12+000.000 – 13+2" is worse than none — so
    // the warnings fold before they would clip either.
    const root = rootRef.current;
    const part = (sel: string) => {
      const el = root?.querySelector<HTMLElement>(sel);
      const w = el?.offsetWidth ?? 0;
      return w > 0 ? w + 8 : 0;
    };
    const identityNeeds = IDENTITY_FLOOR_PX + part('[data-dock-eyebrow]') + part('[data-dock-range]');
    const spare =
      width -
      ROW_CHROME_PX -
      (kpisRef.current?.offsetWidth ?? 0) -
      (buttonsRef.current?.offsetWidth ?? 0) -
      identityNeeds;
    const next = natural > spare;
    setMode((m) => (m.signature === signature && m.collapsed === next ? m : { signature, collapsed: next }));
  });
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (typeof w === 'number' && w > 0) setWidth(Math.round(w));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <DockHeaderWidthContext value={width}>
      {/* The `@container` is this outer box; the row inside queries it. Two
          boxes because an element cannot query its own size.

          `relative z-20`: `container-type` implies layout containment, which
          makes this box a stacking context, so anything that overflows it
          un-portalled (a focus ring, a tooltip) would be painted over by the
          tab bar after it (also a container). The header's own popovers —
          VerdictChip's criteria, ⓘ Details — are portalled and unaffected.
          `DockFrame`'s grip sits above it again (z-30). */}
      <div ref={rootRef} data-testid={testId} className={cn('@container relative z-20 shrink-0', className)}>
        {/* Below 560 px (a phone) the row wraps: identity + buttons stay on
            the first line and the KPIs take a full second line, instead of
            the never-hiding priority-0 figures pushing the buttons off the
            dock. */}
        <div className='flex h-10 min-w-0 items-center gap-3 px-3 text-xs @max-[560px]:h-auto @max-[560px]:flex-wrap @max-[560px]:gap-y-1 @max-[560px]:py-1.5'>
          {/* The identity takes the slack and truncates; `min-w-24` is the
              title's floor really held (the warnings collapse before they
              would take it), `overflow-hidden` the clip below it. */}
          <div className='flex min-w-24 flex-1 items-center overflow-hidden'>{identity}</div>
          {/* The warnings: their OWN group, outside the identity's clip, so
              they can never be clipped away with it. */}
          {warningItems.length > 0 &&
            (collapsed ? (
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type='button'
                    data-testid={warningsTestId ?? (testId ? testId.replace(/-header$/, '-warnings') : undefined)}
                    aria-label={t('dock.warnings', {
                      count: warningItems.length,
                      formatted: warningItems.length.toLocaleString(),
                    })}
                    title={t('dock.warnings', {
                      count: warningItems.length,
                      formatted: warningItems.length.toLocaleString(),
                    })}
                    className={cn(
                      'sv-chip sv-chip--warn shrink-0 cursor-pointer text-[10px] whitespace-nowrap',
                      DOCK_CONTROL.focus,
                    )}
                  >
                    <AlertTriangle className='size-3' aria-hidden />
                    <span className='sv-num'>{warningItems.length}</span>
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align='start'
                  collisionPadding={8}
                  data-testid={`${warningsTestId ?? (testId ? testId.replace(/-header$/, '-warnings') : 'dock-warnings')}-menu`}
                  className='sv-palette flex w-80 flex-col gap-2 border-slate-200 bg-white/95 p-3 text-xs text-slate-700 backdrop-blur dark:border-stone-700 dark:bg-stone-900/95 dark:text-stone-200'
                >
                  {warningItems.map((w, i) => {
                    // The chip's tooltip IS its full text; in a popover a
                    // tooltip is a hover away, so it is printed under it.
                    const full =
                      isValidElement<{ title?: unknown }>(w) && typeof w.props.title === 'string'
                        ? w.props.title
                        : null;
                    return (
                      <div key={isValidElement(w) ? (w.key ?? i) : i} className='flex flex-col items-start gap-1'>
                        <div className='flex flex-wrap items-center gap-1.5'>{w}</div>
                        {full && <p className={DOCK_TEXT.metaMuted}>{full}</p>}
                      </div>
                    );
                  })}
                </PopoverContent>
              </Popover>
            ) : (
              <div ref={warnRef} data-dock-warnings className='flex shrink-0 items-center gap-1.5'>
                {warningItems}
              </div>
            ))}
          {kpis != null && (
            <div
              ref={kpisRef}
              className='flex shrink-0 items-center @max-[560px]:order-last @max-[560px]:min-w-0 @max-[560px]:basis-full @max-[560px]:shrink'
            >
              {kpis}
            </div>
          )}
          {buttons != null && (
            <div ref={buttonsRef} className='flex shrink-0 items-center'>
              {buttons}
            </div>
          )}
        </div>
      </div>
    </DockHeaderWidthContext>
  );
};

// ---------------------------------------------------------------- identity

export interface DockIdentityProps {
  /** The instrument — "Clearance", "Station volumes". */
  eyebrow: string;
  /** The run — a profile name, an alignment name. Truncates; full in `title`. */
  title: string;
  titleTestId?: string;
  /** Where — "along Main Rd". Hidden below a 1100 px dock. */
  scope?: ReactNode;
  scopeTestId?: string;
  /** Over what — the formatted station range. Mono, never truncated; hidden below 1100 px. */
  range?: string;
  rangeTestId?: string;
  /** The range's tooltip — what the figures mean ("Station range"). */
  rangeTitle?: string;
}

export const DockIdentity = ({
  eyebrow,
  title,
  titleTestId,
  scope,
  scopeTestId,
  range,
  rangeTestId,
  rangeTitle,
}: DockIdentityProps) => (
  // `items-baseline`: four sizes of text on one line have to share a baseline
  // or the row reads as stepped. The truncating spans are FLEX ITEMS, so their
  // `overflow: hidden` does not move their baseline the way it does an
  // inline-block's (the old header's reason for not truncating).
  // `overflow-hidden`: on a dock narrower than everything needs,
  // the eyebrow + the title's 12ch floor can still be wider than the slack —
  // and without a clip the identity painted UNDER the warning chips that
  // follow (the station-volumes dock at 1000 px with a low-coverage chip).
  <div className='flex min-w-0 flex-1 items-baseline gap-2 overflow-hidden'>
    {/* The eyebrow is the first thing to go on a narrow dock: the dock's
        content already says which instrument it is. */}
    <span
      data-dock-eyebrow
      className='shrink-0 text-[11px] leading-4 tracking-wide text-slate-500 uppercase @max-[760px]:hidden dark:text-stone-400'
    >
      {eyebrow}
    </span>
    <span
      data-testid={titleTestId}
      title={title}
      className='min-w-[12ch] shrink truncate text-[13px] leading-5 font-semibold text-slate-900 dark:text-white'
    >
      {title}
    </span>
    {scope != null && (
      <span data-testid={scopeTestId} className={`min-w-0 shrink-[2] truncate ${DOCK_TEXT.muted} @max-[1100px]:hidden`}>
        {scope}
      </span>
    )}
    {range && (
      <span
        // Whole or not at all: a half-truncated range ("12+000.00 —…") is
        // worse than none, so it never shrinks and instead hides below a
        // 1100 px header (the same rung as the scope); it stays in ⓘ Details.
        data-dock-range
        className='sv-sta shrink-0 text-slate-600 @max-[1100px]:hidden dark:text-stone-300'
        title={rangeTitle}
      >
        <span data-testid={rangeTestId}>{range}</span>
      </span>
    )}
  </div>
);

// ---------------------------------------------------------------- KPI strip

export interface DockKpiStripProps {
  /**
   * In display order. The verdict chip, when there is one, is the first item
   * with priority `0` and NO "Verdict" label — the chip is its own label.
   */
  items: DockKpiItem[];
  testId?: string;
}

/**
 * The header's figures, one row, `gap-5`. Each item is wrapped so the
 * container-query class hides the WHOLE tile (label and value together).
 * `data-kpi-priority` is on the wrapper for tests and for anyone debugging
 * "why did Fill disappear".
 */
export const DockKpiStrip = ({ items, testId }: DockKpiStripProps) => (
  <div
    data-testid={testId}
    className='flex shrink-0 items-center gap-5 @max-[560px]:shrink @max-[560px]:flex-wrap @max-[560px]:gap-x-3 @max-[560px]:gap-y-1'
  >
    {items.map((item) => (
      <div
        key={item.key}
        data-kpi-key={item.key}
        data-kpi-priority={item.priority}
        className={cn('flex shrink-0 items-center', KPI_HIDE_CLASS[item.priority])}
      >
        {item.node}
      </div>
    ))}
  </div>
);

// ------------------------------------------------------------ panel buttons

export interface DockPanelButtonsProps {
  /** `clearance` / `sv`: the buttons are `${prefix}-expand` and `${prefix}-close`. */
  testIdPrefix: string;
  /** The ⓘ — a `<DockDetailsPopover>`. First, so it sits beside the figures it explains. */
  details?: ReactNode;
  expanded: boolean;
  onToggleExpanded: () => void;
  onClose: () => void;
  /** The close button's name — the docks word it per dock ("Close the clearance results"). */
  closeLabel: string;
  /** Defaults: the shared `stationVolumes.panel.expand` / `.collapse` copy. */
  expandLabel?: string;
  collapseLabel?: string;
}

/**
 * ⓘ ⤢ ✕ — what the PANEL does, not what the data does, so they are grouped
 * at the row's right end and look different from the data tools in the tab
 * bar (borderless icon buttons vs outlined text buttons).
 */
export const DockPanelButtons = ({
  testIdPrefix,
  details,
  expanded,
  onToggleExpanded,
  onClose,
  closeLabel,
  expandLabel,
  collapseLabel,
}: DockPanelButtonsProps) => {
  const { t } = useTranslation('project');
  const expandText = expanded
    ? (collapseLabel ?? t('stationVolumes.panel.collapse'))
    : (expandLabel ?? t('stationVolumes.panel.expand'));
  return (
    <div className='-mr-1 flex shrink-0 items-center gap-0.5'>
      {details}
      <button
        data-testid={`${testIdPrefix}-expand`}
        type='button'
        aria-pressed={expanded}
        aria-label={expandText}
        title={expandText}
        onClick={onToggleExpanded}
        className={DOCK_CONTROL.iconButton}
      >
        {expanded ? <Minimize2 className={DOCK_CONTROL.icon} /> : <Maximize2 className={DOCK_CONTROL.icon} />}
      </button>
      <button
        data-testid={`${testIdPrefix}-close`}
        type='button'
        aria-label={closeLabel}
        title={closeLabel}
        onClick={onClose}
        className={DOCK_CONTROL.iconButton}
      >
        <X className={DOCK_CONTROL.icon} />
      </button>
    </div>
  );
};
